import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Stack,
  Typography,
  Button,
  Pagination,
  PaginationItem,
  TextField,
  InputAdornment,
  IconButton,
  Icon
} from 'investira.react.components';

import { PageTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import withResponseHandling from '../../../hoc/withResponseHandling';
import FeederMasterDialog from './FeederMasterDialog';
import FeederMasterList from './FeederMasterList';
import services from '../../../services';
import FeederMasterConfirmDelete from './FeederMasterConfirmDelete';

const MOCKED_RELATIONSHIP_DATA = {
  data: {
    feeders_masters: [{ pessoa_id: 93685, master_pessoa_id: 83350 }],
    pessoas: {
      93685: {
        pessoa_id: 93685,
        pessoa: 'Absolute Alpha Marb Advisory FIC de FIF Multi - Responsabilidade Limitada',
        pessoa_fantasia: 'EU SOU A FEEDER Absolute Alpha Beta Teta Zulu Pro Max',
        cnpj: '35617938000130',
        setor: 'Fundos de Investimento, Exceto Previdenciários e Imobiliários',
        logo_ok: true,
        natureza: '2224',
        setor_id: '64.70.1.01',
        top_setor: 'Financeiro',
        logo_pessoa_id: 59842,
        pessoa_tipo_id: 0
      },
      83350: {
        pessoa_id: 83350,
        pessoa: 'MASTER Absolute Alpha Beta Teta Zulu Pro Max',
        pessoa_fantasia: 'EU SOU O MASTER Absolute Alpha Beta Teta Zulu Pro Max',
        cnpj: '35617938000130',
        setor: 'Fundos de Investimento, Exceto Previdenciários e Imobiliários',
        logo_ok: true,
        setor_id: '64.70.1.01',
        top_setor: 'Financeiro',
        logo_pessoa_id: 59842,
        pessoa_tipo_id: 0
      }
    }
  }
};

const buildPessoasById = pPessoas =>
  Object.values(pPessoas || {}).reduce((pAcc, pPessoa) => {
    const xPessoaId = Number(pPessoa?.pessoa_id);

    if (!Number.isFinite(xPessoaId)) {
      return pAcc;
    }

    return {
      ...pAcc,
      [String(xPessoaId)]: pPessoa
    };
  }, {});

const FeederMaster = memo(props => {
  const PAGE_SIZE = 20;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [feedersMaster, setFeedersMaster] = useState([]);
  const [pessoasById, setPessoasById] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  useEffect(() => {
    feederList(1);
  }, []);

  const feederList = (pPage, pSearch = appliedSearch) => {
    const xPage = Number(pPage) || 1;
    const xSearch = (pSearch || '').trim();
    const xParams = {
      page: xPage,
      size: PAGE_SIZE
    };

    if (xSearch) {
      xParams.pesquisa = xSearch;
    }

    setIsLoading(true);
    services.pessoas.feederList(
      xParams,
      rRes => {
        if (rRes?.data) {
          setFeedersMaster(rRes.data.feeders_masters);
          setPessoasById(buildPessoasById(rRes.data.pessoas));
          setCurrentPage(xPage);
          setTotalPages(Number(rRes?.pages?.total_pages) || 1);
          setAppliedSearch(xSearch);
        }
      },
      rErr => {
        console.log(rErr);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  const handleSubmitSuccess = pPayload => {
    const { pessoa_id, master_pessoa_id } = pPayload;
    const xPessoaId = Number(pessoa_id);
    const xMasterPessoaId = Number(master_pessoa_id);

    if (!Number.isFinite(xPessoaId) || !Number.isFinite(xMasterPessoaId)) {
      return;
    }

    services.pessoas.feederModify(
      {
        pessoa_id: xPessoaId,
        master_pessoa_id: xMasterPessoaId
      },
      () => {
        feederList(currentPage);
      },
      rErr => {}
    );

    handleClearValues();
  };

  const handleModify = pValues => {
    setDialogOpen(true);
    setDefaultValues(pValues);
  };

  const handleClickRemove = pValues => {
    setDefaultValues(pValues);
    setOpenDeleteConfirm(true);
  };

  const handleRemove = pRelationship => {
    const { pessoa_id } = pRelationship;
    const xPessoaId = Number(pessoa_id);

    if (!Number.isFinite(xPessoaId)) {
      return;
    }

    services.pessoas.feederModify(
      {
        pessoa_id: xPessoaId,
        master_pessoa_id: null
      },
      () => {
        feederList(currentPage);
      },
      rErr => {}
    );

    handleClearValues();
  };

  const handleCancel = () => {
    setDialogOpen(false);
    handleClearValues();
  };

  const handleCancelDelete = () => {
    setOpenDeleteConfirm(false);
    handleClearValues();
  };

  const handleClearValues = () => {
    setDefaultValues({});
  };

  const handleSubmitSearch = () => {
    feederList(1, searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    feederList(1, '');
  };

  const handleSearchKeyDown = pEvent => {
    if (pEvent.key === 'Enter') {
      pEvent.preventDefault();
      handleSubmitSearch();
    }
  };

  return (
    <PageTemplate
      title="Feeder Master"
      description="Gerencie os vínculos entre fundos."
      maxWidth="lg"
      headerRenderActions={
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={() => setDialogOpen(true)}
            startIcon={<Icon iconName="insert" size={18} />}>
            Novo Vínculo
          </Button>
        </Stack>
      }>
      <Stack gap={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}>
          <Stack direction="row" gap={1} flexGrow={1}>
            <TextField
              fullWidth
              label="Buscar feeder/master"
              size="small"
              variant="outlined"
              value={searchInput}
              onChange={pEvent => setSearchInput(pEvent.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Nome, razão social ou CNPJ"
              InputProps={{
                endAdornment: !searchInput ? null : (
                  <InputAdornment position="end">
                    <IconButton size="small" color="primary" onClick={handleClearSearch}>
                      <Icon iconName="cancel" size={12} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <Button
              variant="contained"
              onClick={handleSubmitSearch}
              disabled={isLoading}
              sx={{ px: 2 }}
              startIcon={<Icon iconName="find" size={18} />}>
              Buscar
            </Button>
          </Stack>
        </Stack>

        <FeederMasterList
          feedersMaster={feedersMaster}
          pessoasById={pessoasById}
          onRemove={handleClickRemove}
          isLoading={isLoading}
          onModify={handleModify}
        />

        {totalPages > 1 && (
          <Stack alignItems="center">
            <Pagination
              page={currentPage}
              count={totalPages}
              color="primary"
              shape="rounded"
              size="large"
              showFirstButton
              showLastButton
              onChange={(_pEvent, pPage) => feederList(pPage)}
              renderItem={pItem => {
                return <PaginationItem {...pItem} />;
              }}
            />
          </Stack>
        )}

        <FeederMasterDialog
          open={dialogOpen}
          onClose={handleCancel}
          onSubmitSuccess={handleSubmitSuccess}
          onMessageSuccess={props.onMessageSuccess}
          responseErrorHandling={props.responseErrorHandling}
          offline={props.offline}
          defaultValues={defaultValues}
        />

        <FeederMasterConfirmDelete
          open={openDeleteConfirm}
          feeder={defaultValues?.feeder}
          master={defaultValues?.master}
          onRemove={handleRemove}
          onClose={handleCancelDelete}
        />
      </Stack>
    </PageTemplate>
  );
});

FeederMaster.propTypes = {
  id: PropTypes.string,
  offline: PropTypes.func,
  onBack: PropTypes.func,
  onOpenDialog: PropTypes.func,
  onMessageSuccess: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  responseErrorHandling: PropTypes.func
  // defaultValues: PropTypes.object
};

FeederMaster.defaultProps = {
  id: 'feeder-master',
  onSubmitSuccess: () => {}
};

FeederMaster.displayName = 'FeederMaster';

export default withResponseHandling(withMessage(FeederMaster));
